"use client"

import "assets/main.css"
import "assets/base.scss"
import "assets/swiper.scss"
import "assets/loading.scss"
import "swiper/css"
import { Head } from "components/common"
import { StoreProvider } from "components/ui/Context"
import { GoogleOAuthProvider } from "@react-oauth/google"
import ModalLogin from "components/ModalLogin"
import ModalBan from "components/ModalBan"
import NextNprogress from "nextjs-progressbar"
import KEY from "utils/Const"
import EventButton from "components/EventButton"
import { apiUser } from "api"
import { useRouter } from "next/router"
import { useTracking } from "hooks/useTracking"
import { useEffect, useRef, useState } from "react"
import { OIDC_CALLBACK_PAYLOAD_KEY, clearOidcTransaction, handleLogin } from "utils/base"
import { getAccessToken, getRefreshToken, removeToken } from "lib/Cookies"
import useCompleteLogin from "components/ModalLogin/useCompleteLogin"
import { getAuthError, kickoutDevice, loginWithProvider } from "components/ModalLogin/authService"
import { Modal, notification } from "components/ui"
import useStore from "components/ui/Context"

if (process.env.NODE_ENV !== "development") {
  console.log = () => {}
  console.debug = () => {}
  console.info = () => {}
}

const Noop = ({ children }) => <>{children}</>
let hasCheckedInitialLoginParam = false

function MyApp({ Component, pageProps }) {
  const Layout = Component.Layout || Noop
  const router = useRouter()

  // Khởi tạo tracking với custom hook
  useTracking(router)

  useEffect(() => {
    // window.Countly = Countly
    // Countly.init({
    //   app_key: process.env.NEXT_PUBLIC_TRACKING_KEY,
    //   url: "https://tracking.api.vinasports.com.vn"
    // })
  }, [])

  // console.log(messaging, " hien thi messgaing")
  return (
    <>
      <NextNprogress
        color="#52b5f9"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{ easing: "ease", speed: 500, showSpinner: false }}
      />
      <Head />
      <StoreProvider>
        <GoogleOAuthProvider clientId={KEY.KEY_GOOGLE}>
          <AutoOnliveRedirectLogin />
          <Layout pageProps={pageProps}>
            <Component {...pageProps} />
            <EventButton />
          </Layout>
          <ModalLogin />
          <ModalBan />
          <div id="fb-root"></div>
          <div id="modal-root"></div>
          <div id="toast-root"></div>
        </GoogleOAuthProvider>
      </StoreProvider>
    </>
  )
}

function AutoOnliveRedirectLogin() {
  const { logOut } = useStore()
  const [pendingLogin, setPendingLogin] = useState(null)
  const [isResolvingDevice, setIsResolvingDevice] = useState(false)
  const isSubmittingOidc = useRef(false)
  const processedOidcStates = useRef({})
  const completeLogin = useCompleteLogin({ initView: "auto" })

  function clearPendingLogin() {
    clearOidcTransaction(pendingLogin?.state)
    setPendingLogin(null)
  }

  async function handleOidcAuthCode(payload) {
    const { code, state } = payload || {}
    if (!code || !state || isSubmittingOidc.current || processedOidcStates.current[state]) return

    isSubmittingOidc.current = true
    processedOidcStates.current[state] = true

    try {
      const response = await loginWithProvider({
        providerName: "onlive",
        providerAccessToken: code
      })

      if (!response?.data) throw new Error("Invalid login data")

      await completeLogin(response.data, "onlive")
      clearOidcTransaction(state)
    } catch (error) {
      const authError = getAuthError(error)
      if (authError.errorCode === 411) {
        setPendingLogin({
          type: "oidc",
          uuid: authError.data?.uuid,
          message: authError.message,
          userId: authError.data?.user_id,
          state
        })
      } else {
        clearOidcTransaction(state)
        notification.error({ message: authError.message })
      }
    } finally {
      isSubmittingOidc.current = false
    }
  }

  async function onKickDevice() {
    if (!pendingLogin || isResolvingDevice) return

    setIsResolvingDevice(true)
    try {
      const kickoutResponse = await kickoutDevice(pendingLogin.uuid)
      if (kickoutResponse?.error_code !== 200) {
        throw new Error(kickoutResponse?.error_message || "Can not kick out old device")
      }

      clearOidcTransaction(pendingLogin.state)
      setPendingLogin(null)
      handleLogin({ redirectUrl: process.env.NEXT_PUBLIC_REDIRECT_URL, mode: "redirect" })
    } catch (error) {
      const authError = getAuthError(error)
      notification.error({ message: authError.message })
    } finally {
      setIsResolvingDevice(false)
    }
  }

  async function forceLogout() {
    if (!getAccessToken()) return

    try {
      await apiUser.logOut({ refresh_token: getRefreshToken() })
    } catch (error) {
      console.log(error)
    }

    removeToken()
    logOut()

    try {
      const channel = new BroadcastChannel("auth_channel")
      channel.postMessage({ type: "LOGOUT" })
      channel.close()
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const callbackPayload = sessionStorage.getItem(OIDC_CALLBACK_PAYLOAD_KEY)
    if (callbackPayload) {
      sessionStorage.removeItem(OIDC_CALLBACK_PAYLOAD_KEY)
      try {
        const payload = JSON.parse(callbackPayload)
        handleOidcAuthCode(payload)
      } catch (error) {
        notification.error({ message: "Invalid OIDC callback data" })
      }
      return
    }

    if (hasCheckedInitialLoginParam) return

    hasCheckedInitialLoginParam = true

    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get("login") === "onlive") {
      ;(async () => {
        await forceLogout() // Ensure logged out before starting new oidc login flow
        handleLogin({ redirectUrl: process.env.NEXT_PUBLIC_REDIRECT_URL, mode: "redirect" })
      })()
    }
  }, [])

  return (
    <Modal isOpen={Boolean(pendingLogin)} onCancel={clearPendingLogin}>
      <div className="max-w-[464px] p-8">
        <div className="text-2xl font-semibold text-center mb-2">Thong bao</div>
        <div className="text-lg text-center">
          <div className="text-dark-300">
            Tai khoan cua ban da dang nhap qua 3 thiet bi. Vui long chon Tiep tuc de dang xuat khoi thiet bi cu nhat.
          </div>
          <hr className="mt-6" style={{ borderTop: "solid 0.5px", opacity: "0.3" }} />
          <div className="mt-4">
            <button disabled={isResolvingDevice} className="px-2 mr-8 font-medium" onClick={clearPendingLogin}>
              Huy
            </button>
            <button disabled={isResolvingDevice} className="px-2 text-primary-500 font-medium" onClick={onKickDevice}>
              {isResolvingDevice ? "Dang xu ly..." : "Tiep tuc"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MyApp
